import { Button } from 'primereact/button'
import { Dialog } from 'primereact/dialog'
import classes from './ProjectCreationRestrictionModal.module.scss'
import { ProjectCreationRestrictionModel } from './ProjectCreationRestrictionModel'
import { DataTable } from 'primereact/datatable'
import { Column, ColumnBodyOptions } from 'primereact/column'

interface ProjectCreationRestrictionModalProps {
  onClose: () => void
  visible: boolean
  projectCreationRestrictionModel: ProjectCreationRestrictionModel
}

const ProjectCreationRestrictionModal = (props: ProjectCreationRestrictionModalProps) => {
  const { isInvalidFlexHandler, projectLineModel, newHandler } =
    props.projectCreationRestrictionModel

  const headerTemplate = () => {
    return (
      <h3 className={classes['dialog-header__title']}>
        {props.projectCreationRestrictionModel.hasMultipleServiceLines &&
          !props.projectCreationRestrictionModel.hasInvalidIndustryCode && (
            <label>FLEX Project With Multiple Order Lines</label>
          )}

        {props.projectCreationRestrictionModel.hasInvalidIndustryCode &&
          !props.projectCreationRestrictionModel.hasMultipleServiceLines && (
            <label>Invalid Industry Code</label>
          )}

        {props.projectCreationRestrictionModel.hasInvalidIndustryCode &&
          props.projectCreationRestrictionModel.hasMultipleServiceLines && (
            <label>Invalid Industry Code and Multiple Order Lines Detected</label>
          )}

        {isInvalidFlexHandler && <label>FLEX Project Handler Mismatch</label>}
      </h3>
    )
  }

  const footerTemplate = () => {
    return (
      <div className={classes['dialog-footer']}>
        <Button onClick={props.onClose} className={classes['btn-ok']} label={'OK'} />
      </div>
    )
  }

  const invalidIndustryCodeMessage = (
    <>
      <label className={classes['verbiage']}>
        The GMAWB project cannot be created, because the selected order line(s) contain the wrong
        Industry code. Only the following order lines are valid for GMAWB Project Creation:
      </label>
      <label className={classes['verbiage']}>
        For Service Lines 256, 260 and 712: Industry Code "111" only <br />
        For Service Line 763: any Industry code
      </label>
      <label className={classes['verbiage']}>
        Please reach out to the FLEX Order Creator to correct this.
      </label>
    </>
  )

  const multipleServiceLineMessage = (
    <>
      <label className={classes['verbiage']}>
        The GMAWB project cannot be created, because the current FLEX Project contains multiple GMA
        Order Lines. <br />
      </label>
      <label className={classes['verbiage']}>
        Please reach out to the FLEX Order Creator to request a single FLEX Project per GMA Order
        Line.
      </label>
    </>
  )

  const multipleServiceLineAndInvalidIndustryCodeMessage = (
    <>
      <label className={classes['verbiage']}>
        <strong>The GMAWB project cannot be created due to the following issues:</strong>
        <br />
        <label style={{ textIndent: '1rem' }}>
          1. The selected order line(s) contain the wrong Industry code. <br />
          2. The current FLEX Project contains multiple GMA Order Lines.
        </label>
      </label>

      <label className={classes['verbiage']}>
        <strong>Please note the following requirements for valid GMAWB Project creation:</strong>
        <br />
        <label style={{ textIndent: '1rem' }}>
          - For Service Lines 256, 260 and 712: Industry Code "111" only <br />- For Service Line
          763: any Industry code
        </label>
      </label>

      <label className={classes['verbiage']}>
        <strong>Action Required:</strong>
        <br />
        <label style={{ textIndent: '1rem' }}>
          - Contact the FLEX Order Creator to correct the Industry code(s). <br />- Request a single
          FLEX Project per GMA Order Line.
        </label>
      </label>

      <label className={classes['verbiage']}>
        Once these issues are resolved, you can proceed with the GMAWB project creation.
      </label>
    </>
  )

  const colTemplate = (data: any, opt: ColumnBodyOptions) => (
    <div
      className={`${classes['col-text-div']}
          } ul-grid__column`}
    >
      {data[opt.field]}
    </div>
  )

  const renderInvalidFlexHandlerTable = () => {
    const invalidProjectLines = projectLineModel?.filter(
      x => x.flexHandler.toLocaleLowerCase() !== newHandler
    )

    return (
      <>
        <DataTable
          size="small"
          showGridlines
          value={invalidProjectLines}
          scrollable
          scrollHeight="200px"
        >
          <Column
            header="Order Line Number"
            field="orderLineNumberDescription"
            body={colTemplate}
          />
        </DataTable>
      </>
    )
  }

  const invalidFlexHandlerMessage = (
    <>
      {projectLineModel && projectLineModel?.length > 1 ? (
        <label className={classes['verbiage-flex-handler']}>
          Unable to proceed with the GMAWB Project because you are not the FLEX project handler of
          one or more of the selected Order Lines. You will need to reassign yourself as the FLEX
          Project Handler before proceeding.
        </label>
      ) : (
        <label className={classes['verbiage-flex-handler']}>
          Unable to proceed with the GMAWB Project because you are not the FLEX project handler of
          the selected Order Line. You will need to reassign yourself as the FLEX Project Handler
          before proceeding.
        </label>
      )}

      {projectLineModel && projectLineModel?.length > 1 && renderInvalidFlexHandlerTable()}

      <label className={classes['verbiage-flex-handler']}>
        <strong>How to reassign FLEX Handler:</strong>
        <br />
        Locate FLEX Handler column in Project Work Queue and double-click the current FLEX Project
        Handler Name to select a new value, click the Green check icon to save.
      </label>

      <label className={classes['verbiage-flex-handler']}>
        For bulk updates, select multiple checkboxes and click the pencil icon on the FLEX Handler
        column header.
      </label>

      <label className={classes['verbiage-flex-handler']}>
        <i>
          Reminder: Creating the GMAWB project results in a FLEX Template Confirmation step in
          Choose Delivery Path and the ULS requirement is that the assigned FLEX project handler
          confirms the template.
        </i>
      </label>
    </>
  )

  return (
    <Dialog
      className={classes['dialog']}
      style={{
        width: isInvalidFlexHandler ? '56rem' : '45rem',
      }}
      visible={props.visible}
      onHide={props.onClose}
      header={headerTemplate}
      footer={footerTemplate}
    >
      <div className={classes['dialog-body']}>
        {props.projectCreationRestrictionModel.hasMultipleServiceLines &&
          !props.projectCreationRestrictionModel.hasInvalidIndustryCode &&
          multipleServiceLineMessage}

        {props.projectCreationRestrictionModel.hasInvalidIndustryCode &&
          !props.projectCreationRestrictionModel.hasMultipleServiceLines &&
          invalidIndustryCodeMessage}

        {props.projectCreationRestrictionModel.hasInvalidIndustryCode &&
          props.projectCreationRestrictionModel.hasMultipleServiceLines &&
          multipleServiceLineAndInvalidIndustryCodeMessage}

        {props.projectCreationRestrictionModel.isInvalidFlexHandler && invalidFlexHandlerMessage}
      </div>
    </Dialog>
  )
}

export default ProjectCreationRestrictionModal
